import { AxiosResponse } from "axios";

import {
  IAdminUser,
  IPermissionCatalogItem,
  IRoleWithPermissions,
} from "../../@types/admin/AdminAccess";
import { IPaginatedResponse } from "../../@types/PaginatedResponse";
import { api } from "../api";

/**
 * Access-management admin pages (EN-5129, portal-only — never existed in
 * Retool): Role Permissions (role ↔ permission grants) and User Access
 * (user roles + direct permission grants). Each page calls its own
 * page-scoped controller so the per-page permissions stay clean.
 */
export const accessManagementService = {
  // ---- Role Permissions page ----
  async getRolesWithPermissions(): Promise<
    AxiosResponse<IRoleWithPermissions[]>
  > {
    return api.get<IRoleWithPermissions[]>(
      "/Retool/Admin/RolePermissions/Roles"
    );
  },

  async getRolePermissionCatalog(): Promise<
    AxiosResponse<IPermissionCatalogItem[]>
  > {
    return api.get<IPermissionCatalogItem[]>(
      "/Retool/Admin/RolePermissions/Permissions"
    );
  },

  async grantRolePermission(
    roleCode: string,
    permissionCode: string
  ): Promise<AxiosResponse<void>> {
    return api.post<void>(
      `/Retool/Admin/RolePermissions/Roles/${roleCode}/Permissions`,
      { permissionCode }
    );
  },

  async revokeRolePermission(
    roleCode: string,
    permissionCode: string
  ): Promise<AxiosResponse<void>> {
    return api.post<void>(
      `/Retool/Admin/RolePermissions/Roles/${roleCode}/Permissions/Revoke`,
      { permissionCode }
    );
  },

  // ---- User Access page ----
  async getUsers(
    search: string,
    page: number,
    pageSize: number
  ): Promise<AxiosResponse<IPaginatedResponse<IAdminUser[]>>> {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    params.append("page", page.toString());
    params.append("pageSize", pageSize.toString());
    return api.get<IPaginatedResponse<IAdminUser[]>>(
      "/Retool/Admin/UserAccess/Users",
      { params }
    );
  },

  async getUserAccessRoles(): Promise<AxiosResponse<IRoleWithPermissions[]>> {
    return api.get<IRoleWithPermissions[]>("/Retool/Admin/UserAccess/Roles");
  },

  async getUserAccessPermissionCatalog(): Promise<
    AxiosResponse<IPermissionCatalogItem[]>
  > {
    return api.get<IPermissionCatalogItem[]>(
      "/Retool/Admin/UserAccess/Permissions"
    );
  },

  async getUserDirectPermissions(
    cmsUserId: number
  ): Promise<AxiosResponse<string[]>> {
    return api.get<string[]>(
      `/Retool/Admin/UserAccess/Users/${cmsUserId}/Permissions`
    );
  },

  async assignUserRole(
    cmsUserId: number,
    roleCode: string
  ): Promise<AxiosResponse<void>> {
    return api.post<void>(`/Retool/Admin/UserAccess/Users/${cmsUserId}/Role`, {
      roleCode,
    });
  },

  async grantUserPermission(
    cmsUserId: number,
    permissionCode: string
  ): Promise<AxiosResponse<void>> {
    return api.post<void>(
      `/Retool/Admin/UserAccess/Users/${cmsUserId}/Permissions`,
      { permissionCode }
    );
  },

  async revokeUserPermission(
    cmsUserId: number,
    permissionCode: string
  ): Promise<AxiosResponse<void>> {
    return api.post<void>(
      `/Retool/Admin/UserAccess/Users/${cmsUserId}/Permissions/Revoke`,
      { permissionCode }
    );
  },
};
